import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

// Material Icons
import TimerIcon from '@mui/icons-material/Timer';
import StorageIcon from '@mui/icons-material/Storage';
import BufferingIcon from '@mui/icons-material/Memory';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LinearProgressBarWithMarkers from '../../Library/LinearProgressBarWithMarkers';
import { grey } from '@mui/material/colors';
import { useAiChatSessionStore } from '../AiChatContext';
import { ConnectionState } from '@lib/models/connectionState';
import type { SessionInformation } from '@lib/models/semanticKernelModel/models';
import { useTheme } from '@mui/material/styles';
import { Timer } from '@mui/icons-material';
import { PauseIcon, StopIcon } from '../../v2/icons';

const MONO_FONT = '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace';

/**
 * Optional inline Pause / Stop control mounted at the right end of the
 * stats row. When `pauseControl` is provided, StreamStatistics shows a
 * single state-driven button instead of a separate footer:
 *
 *   - turnOpen=false                → disabled Pause icon
 *   - turnOpen=true, suspended=false → Pause icon (clickable, tooltip)
 *   - suspended=true                 → Stop icon (cancel)
 *
 * Hosts wire `onPause` (suspend / resume) and `onCancel` (stop stream)
 * to the gateway. The component picks which one to call based on state.
 */
export interface StreamStatisticsPauseControl {
    turnOpen: boolean;
    suspended: boolean;
    suspending?: boolean;
    onPause: () => void;
    onCancel: () => void;
}

export interface StreamStatisticsProps {
    /**
     * Compact v2 chrome — mono font everywhere, hides elapsed time,
     * renders a circle badge for session-size usage instead of a
     * progress bar. Defaults to `false` (legacy v1 chrome).
     */
    compact?: boolean;
    /** Optional inline Pause/Stop button. */
    pauseControl?: StreamStatisticsPauseControl;
}

/**
 * Small circular badge showing the integer % of the session-size
 * threshold currently used. Tooltip exposes the full numbers (size,
 * maxSize, threshold) so the bar's information stays accessible.
 */
function SessionUsageBadge({ info }: { info: SessionInformation }) {
    const theme = useTheme();
    const integerPercent = Math.round(info.ratio * 100);
    const overThreshold = info.ratio >= info.threshold;
    const tooltipBody = (
        <Box sx={{ fontFamily: MONO_FONT, fontSize: 11, lineHeight: 1.5 }}>
            <Box sx={{ fontFamily: 'inherit', fontWeight: 500, mb: 0.25 }}>Tamanho do Contexto</Box>
            <Box>{`Uso atual: ${integerPercent}%`}</Box>
            <Box>{`${info.size.toLocaleString()} / ${info.maxSize.toLocaleString()} tokens`}</Box>
            <Box>{`Limite: ${Math.round(info.threshold * 100)}%`}</Box>
        </Box>
    );

    // Progress-arc geometry. The ring is drawn as an SVG circle with a
    // dashed stroke whose dash-length encodes the filled portion of the
    // circumference. Rotating the SVG -90° puts the dash start at 12
    // o'clock, so the arc fills clockwise like a typical progress ring.
    const SIZE = 26;
    const STROKE = 2;
    const RADIUS = (SIZE - STROKE) / 2;
    const CIRC = 2 * Math.PI * RADIUS;
    const clampedRatio = Math.max(0, Math.min(1, info.ratio));
    const dashFilled = CIRC * clampedRatio;
    const trackColor = '#26262d';
    const arcColor = overThreshold ? '#d97a7a' : theme.palette.primary.light;
    const textColor = overThreshold ? '#d97a7a' : '#ececef';

    return (
        <Tooltip title={tooltipBody} placement="top" arrow>
            <Box
                sx={{
                    position: 'relative',
                    width: SIZE,
                    height: SIZE,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
                aria-label={`Tamanho do contexto: ${integerPercent}%`}
            >
                <Box
                    component="svg"
                    width={SIZE}
                    height={SIZE}
                    viewBox={`0 0 ${SIZE} ${SIZE}`}
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        transform: 'rotate(-90deg)',
                    }}
                >
                    {/* Background ring + filled inner disc */}
                    <circle
                        cx={SIZE / 2}
                        cy={SIZE / 2}
                        r={RADIUS}
                        fill="#17171b"
                        stroke={trackColor}
                        strokeWidth={STROKE}
                    />
                    {/* Progress arc */}
                    <circle
                        cx={SIZE / 2}
                        cy={SIZE / 2}
                        r={RADIUS}
                        fill="none"
                        stroke={arcColor}
                        strokeWidth={STROKE}
                        strokeLinecap="round"
                        strokeDasharray={`${dashFilled} ${CIRC}`}
                        style={{ transition: 'stroke-dasharray .25s ease' }}
                    />
                </Box>
                <Box
                    sx={{
                        position: 'relative',
                        fontFamily: MONO_FONT,
                        fontSize: 10.5,
                        fontWeight: 500,
                        color: textColor,
                        lineHeight: 1,
                    }}
                >
                    {integerPercent}
                </Box>
            </Box>
        </Tooltip>
    );
}

export const StreamStatistics: React.FC<StreamStatisticsProps> = ({
    compact = false,
    pauseControl,
}) => {
    const initialSessionInformation: SessionInformation = {
        size: 0,
        originalSize: 0,
        ratio: 0,
        previousRatio: 0,
        threshold: 0.9,
        maxSize: 0,
    }
    const [bufferSize, setBufferSize] = useState<number>(0);

    const theme = useTheme();

    const [sessionInformation, setSessionInformation] = useState<SessionInformation>(initialSessionInformation);
    const currentSession = useAiChatSessionStore((state) => state.session);
    const streamStats = useAiChatSessionStore((state) => state.statistics);
    const connectionStatus = useAiChatSessionStore((state) => state.connectionStatus);

    useEffect(() => {
        setSessionInformation(initialSessionInformation);
    }, [currentSession])

    useEffect(() => {
        const sessionInformation = streamStats?.sessionInformation;
        if (sessionInformation) {
            setSessionInformation(sessionInformation);
        }
    }, [streamStats])

    useEffect(() => {
        // Calculate buffering info
        if (!streamStats) return;
        setBufferSize(streamStats.buffer.reduce((acc, curr) => acc + curr.length, 0))

    }, [streamStats])

    // Pre-existing precedence bug: `?? 0 > 1` parses as `?? (0 > 1)`,
    // so when `buffer.length === 0` this resolved to `0` (numeric, not
    // boolean) and `{showBuffering && (...)}` rendered a stray `0`
    // glyph in the row. Parenthesise the nullish coalesce so we get
    // a real boolean.
    const showBuffering = (streamStats?.buffer.length ?? 0) > 1;
    // Calculate unknown/unparsed count
    const problemCount = (streamStats?.unknonwCount ?? 0) + (streamStats?.unparsedCount ?? 0);
    const showProblems = problemCount > 0;

    // Helper function to format elapsed time
    const formatElapsed = (elapsed: number): string => {
        if (elapsed < 60) {
            return `${elapsed.toFixed(2)}s`;
        } else {
            const minutes = Math.floor(elapsed / 60);
            const seconds = elapsed % 60;
            return `${minutes}.${Math.floor(seconds / 6)}min`;
        }
    };

    // Helper function to format file size
    const formatFileSize = useCallback((bytes: number): string => {
        if (bytes < 1024) {
            return `${bytes.toFixed(1)}B`;
        } else if (bytes < 1024 * 1024) {
            return `${(bytes / 1024).toFixed(1)}KB`;
        } else {
            return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
        }
    }, [streamStats]);

    const getPerSecondColor = () => {
        const tokensPerSecond = streamStats?.tokensPerSecond ?? 0;
        const highest = 1000 / 50;

        if (tokensPerSecond > highest) {
            return theme.palette.warning.dark;
        }
        else if (tokensPerSecond > highest * 0.8) {
            return theme.palette.secondary.light;
        }
        else if (tokensPerSecond > highest * 0.6) {
            return theme.palette.secondary.dark;
        }
        else if (tokensPerSecond > highest * 0.4) {
            return theme.palette.primary.dark;
        }
        else if (tokensPerSecond > highest * 0.2) {
            return theme.palette.text.primary;
        }
        else {
            return theme.palette.text.secondary;
        }
    }

    const getGreyScale = useCallback((ratio: number) => {
        type greyScale = 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;
        const scale = (900 - (Math.min(Math.round((1000 * Math.max(ratio, 0.2)) / 100), 9) * 100) + 100) as greyScale
        return grey[scale]
    }, [sessionInformation]);

    // Apply the mono font to every Typography in the row when in compact
    // (v2) mode. Centralising the override here keeps the per-segment sx
    // blocks below readable.
    const captionSx = compact
        ? { fontFamily: MONO_FONT, fontSize: '0.7rem' }
        : { fontSize: '0.7rem' };

    // Inline Pause/Stop button — only rendered when the host opts in via
    // `pauseControl`. Disabled when no turn is in flight; switches to a
    // Stop glyph once the agent is suspended, so a single button covers
    // pause / resume / stop in one place.
    const renderPauseButton = () => {
        if (!pauseControl) return null;
        const { turnOpen, suspended, suspending, onPause, onCancel } = pauseControl;
        const showStop = suspended;
        const enabled = turnOpen || suspended;
        const handleClick = () => { showStop ? onCancel() : onPause(); };
        const tooltipText = showStop
            ? 'Cancelar — encerra o turno em execução'
            : suspending
                ? 'Pausando…'
                : 'Para continuar envie uma nova mensagem; o agente vai seguir do ponto que parou';
        return (
            <Tooltip title={tooltipText} placement="top" arrow>
                <Box component="span" sx={{ display: 'inline-flex' }}>
                    <IconButton
                        size="small"
                        onClick={handleClick}
                        disabled={!enabled || suspending}
                        sx={{
                            p: 0.5,
                            color: showStop ? '#d97a7a' : '#e0a458',
                            opacity: enabled ? 1 : 0.4,
                            '&:hover': enabled ? { backgroundColor: '#1d1d22' } : undefined,
                        }}
                        aria-label={showStop ? 'Cancelar turno' : 'Pausar turno'}
                    >
                        {showStop ? <StopIcon size={14} /> : <PauseIcon size={14} />}
                    </IconButton>
                </Box>
            </Tooltip>
        );
    };

    return (

        <Box
            sx={{
                paddingTop: 0.5,
                paddingBottom: 0.5,
                paddingLeft: 1,
                paddingRight: 1,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                alignContent: 'center',
                bgcolor: 'rgba(255, 255, 255, 0.02)',
            }}
        >
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: '100%' }}>
                {connectionStatus == ConnectionState.Connected && (
                    <>
                        {/* Event Count */}
                        <Timer sx={{ fontSize: '0.8rem', color: getPerSecondColor() }} />
                        <Typography
                            variant="caption"
                            sx={{ color: getPerSecondColor(), ...captionSx }}
                        >
                            {streamStats?.tokensPerSecond.toFixed(2) ?? "-"}
                        </Typography>

                        <Box sx={{ width: '5px' }} />

                        {/* Elapsed Time — hidden in compact (v2) mode
                            because the panel header already shows the
                            turn elapsed time, making this redundant. */}
                        {!compact && (
                            <>
                                <TimerIcon sx={{ fontSize: '0.8rem', color: 'text.secondary' }} />
                                <Typography
                                    variant="caption"
                                    sx={{ color: 'text.secondary', ...captionSx }}
                                >
                                    {formatElapsed(streamStats?.elapsed ?? 0)}
                                </Typography>

                                <Box sx={{ width: '5px' }} />
                            </>
                        )}

                        {/* Total Transferred */}
                        <StorageIcon sx={{ fontSize: '0.8rem', color: 'text.secondary' }} />
                        <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', ...captionSx }}
                        >
                            {formatFileSize(streamStats?.totalTransfered ?? 0)}
                        </Typography>

                        <Box sx={{ width: '5px' }} />

                        {/* Conditional Buffering */}
                        {showBuffering && (
                            <>
                                <Box sx={{ width: '5px' }} />
                                <BufferingIcon sx={{ fontSize: '0.8rem', color: 'text.primary' }} />
                                <Typography
                                    variant="caption"
                                    sx={{ color: 'text.primary', fontStyle: 'italic', ...captionSx }}
                                >
                                    {formatFileSize(bufferSize)}
                                </Typography>
                            </>
                        )}


                        {/* Conditional Unknown/Unparsed Count */}
                        {showProblems && (
                            <>
                                <Box sx={{ width: '5px' }} />
                                <HelpOutlineIcon sx={{ fontSize: '0.8rem', color: 'text.secondary' }} />
                                <Typography
                                    variant="caption"
                                    sx={{ color: 'text.secondary', ...captionSx }}
                                >
                                    {problemCount}
                                </Typography>
                            </>
                        )}
                    </>
                )}

                {(() => {
                    // Only surface the session-size readout once it
                    // would render as something meaningful. The store
                    // seeds `size` early and `ratio` can be a tiny
                    // positive number (e.g. 0.003 → rounds to 0%) for
                    // the first turns of a fresh session — without
                    // guarding on the rendered integer the compact
                    // badge ends up showing "0", which conveys nothing
                    // useful. Wait for ≥1% of context to be in use.
                    const integerPercent = sessionInformation
                        ? Math.round(sessionInformation.ratio * 100)
                        : 0;
                    const hasUsage = !!sessionInformation
                        && sessionInformation.size > 0
                        && integerPercent > 0;
                    if (!hasUsage) return null;
                    return compact ? (
                        // v2 compact — replace the wide progress bar with
                        // a small badge that surfaces the integer % and
                        // exposes the full numbers via tooltip.
                        <>
                            <Box sx={{ flex: 1 }} />
                            <SessionUsageBadge info={sessionInformation} />
                        </>
                    ) : (
                        <LinearProgressBarWithMarkers
                            value={sessionInformation.ratio * 100}
                            valueBuffer={sessionInformation.previousRatio * 100}
                            markers={[{ position: sessionInformation.threshold * 100, label: 'Limit', color: 'error.main' }]}
                            sx={{ width: '100%', minWidth: '50px', color: getGreyScale(sessionInformation.ratio) }}
                        />
                    );
                })()}

                {compact && pauseControl && (
                    <>
                        {/* push the button to the right edge when there's
                            no session info badge to the left of it. */}
                        {!(sessionInformation
                            && sessionInformation.size > 0
                            && Math.round(sessionInformation.ratio * 100) > 0) && <Box sx={{ flex: 1 }} />}
                        {renderPauseButton()}
                    </>
                )}
            </Stack>
        </Box>

    );
};

export default StreamStatistics;
